import { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Grid,
  Stack,
  TextField,
  Typography,
  Divider,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import HourglassTopIcon from '@mui/icons-material/HourglassTop';
import EventIcon from '@mui/icons-material/Event';
import { useAuth } from '../../auth/useAuth';
import {
  answerGapQuestion,
  GAP_ANALYSIS_UPDATED_EVENT,
  loadSharedGapQuestionsForPatient,
  type GapDomain,
  type GapSeverity,
  type SharedGapQuestionRecord,
} from '../../utils/patientGapAnalysisStore';
import {
  loadLastPatientCheckinAt,
  PATIENT_CHECKIN_UPDATED_EVENT,
  markPatientCheckinCompleted,
} from '../../utils/patientCheckinStore';
import {
  loadPublishedProtocols,
  PUBLISHED_PROTOCOLS_UPDATED_EVENT,
  type ProtocolCheckInTask,
} from '../../utils/publishedProtocolsStore';

const DOMAIN_META: Record<
  GapDomain,
  { label: string; color: string; chipColor: 'error' | 'info' | 'success' | 'default' | 'secondary' }
> = {
  CLINICAL: { label: 'Clinical', color: '#fdeded', chipColor: 'error' },
  BEHAVIORAL: { label: 'Nutrition', color: '#f3e5f5', chipColor: 'secondary' },
  CULTURAL: { label: 'Cultural', color: '#e3f2fd', chipColor: 'info' },
  FAMILY: { label: 'Family', color: '#e8f5e9', chipColor: 'success' },
  SDOH: { label: 'SDOH', color: '#f5f5f5', chipColor: 'default' },
};

const SEVERITY_CHIP: Record<GapSeverity, { label: string; color: 'error' | 'warning' | 'success' }> = {
  HIGH: { label: 'High Priority', color: 'error' },
  MEDIUM: { label: 'Medium Priority', color: 'warning' },
  LOW: { label: 'Low Priority', color: 'success' },
};

interface CheckinNotification {
  id: string;
  type: 'daily' | 'weekly' | 'monthly';
  label: string;
  dueDate: Date;
  isDue: boolean;
  tasks: ProtocolCheckInTask[];
}

const getCheckinsForPatient = (
  patientId?: string,
  patientName?: string,
  lastCheckinAt?: string
): CheckinNotification[] => {
  const protocols = loadPublishedProtocols();
  const normalizedName = patientName?.trim().toLowerCase() ?? '';
  const activeProtocol = protocols.find((p) => {
    const matchesId = patientId ? p.patientId === patientId : false;
    const matchesName = p.patientName.trim().toLowerCase() === normalizedName;
    return (matchesId || matchesName) && p.status === 'ACTIVE' && p.sharedWithPatient;
  });

  if (!activeProtocol) return [];

  const now = new Date();
  const isSameDay = (left: Date, right: Date) =>
    left.getFullYear() === right.getFullYear() &&
    left.getMonth() === right.getMonth() &&
    left.getDate() === right.getDate();
  const isSameWeek = (left: Date, right: Date) => {
    const getWeekStart = (d: Date) => {
      const day = d.getDay();
      const diffToMonday = day === 0 ? -6 : 1 - day;
      const next = new Date(d);
      next.setHours(0, 0, 0, 0);
      next.setDate(next.getDate() + diffToMonday);
      return next;
    };
    return getWeekStart(left).getTime() === getWeekStart(right).getTime();
  };
  const isSameMonth = (left: Date, right: Date) =>
    left.getFullYear() === right.getFullYear() && left.getMonth() === right.getMonth();

  const lastCheckin = lastCheckinAt ? new Date(lastCheckinAt) : null;
  const checkins: CheckinNotification[] = [];

  const tasksByFreq = (freq: 'daily' | 'weekly' | 'monthly') =>
    (activeProtocol.checkInTasks ?? []).filter((t) => t.frequency === freq);

  if (!lastCheckin || !isSameDay(lastCheckin, now)) {
    checkins.push({
      id: 'daily',
      type: 'daily',
      label: 'Daily Check-in',
      dueDate: now,
      isDue: true,
      tasks: tasksByFreq('daily'),
    });
  }

  if (!lastCheckin || !isSameWeek(lastCheckin, now)) {
    checkins.push({
      id: 'weekly',
      type: 'weekly',
      label: 'Weekly Check-in',
      dueDate: now,
      isDue: true,
      tasks: tasksByFreq('weekly'),
    });
  }

  if (!lastCheckin || !isSameMonth(lastCheckin, now)) {
    checkins.push({
      id: 'monthly',
      type: 'monthly',
      label: 'Monthly Check-in',
      dueDate: now,
      isDue: true,
      tasks: tasksByFreq('monthly'),
    });
  }

  return checkins;
};

export const NotificationsPage = () => {
  const { user } = useAuth();
  const [sharedGapQuestions, setSharedGapQuestions] = useState<SharedGapQuestionRecord[]>([]);
  const [checkins, setCheckins] = useState<CheckinNotification[]>([]);
  const [responseDrafts, setResponseDrafts] = useState<Record<string, string>>({});

  const resolvedPatientName = user?.displayName ?? 'Sarah Johnson';
  const resolvedPatientId = user?.id;

  useEffect(() => {
    const refresh = () => {
      const lastCheckin = loadLastPatientCheckinAt(resolvedPatientId, resolvedPatientName);
      setSharedGapQuestions(loadSharedGapQuestionsForPatient(resolvedPatientId, resolvedPatientName));
      setCheckins(getCheckinsForPatient(resolvedPatientId, resolvedPatientName, lastCheckin));
    };
    refresh();
    window.addEventListener('storage', refresh);
    window.addEventListener(GAP_ANALYSIS_UPDATED_EVENT, refresh);
    window.addEventListener(PATIENT_CHECKIN_UPDATED_EVENT, refresh);
    window.addEventListener(PUBLISHED_PROTOCOLS_UPDATED_EVENT, refresh);
    return () => {
      window.removeEventListener('storage', refresh);
      window.removeEventListener(GAP_ANALYSIS_UPDATED_EVENT, refresh);
      window.removeEventListener(PATIENT_CHECKIN_UPDATED_EVENT, refresh);
      window.removeEventListener(PUBLISHED_PROTOCOLS_UPDATED_EVENT, refresh);
    };
  }, [resolvedPatientId, resolvedPatientName]);

  const handleQuestionSubmit = (record: SharedGapQuestionRecord) => {
    const response = responseDrafts[record.question.id]?.trim();
    if (!response) return;
    answerGapQuestion(record.analysisId, record.question.id, response);
    setSharedGapQuestions(loadSharedGapQuestionsForPatient(resolvedPatientId, resolvedPatientName));
    setResponseDrafts((prev) => ({ ...prev, [record.question.id]: '' }));
  };

  const handleCheckinComplete = () => {
    markPatientCheckinCompleted(resolvedPatientId, resolvedPatientName);
    const lastCheckin = loadLastPatientCheckinAt(resolvedPatientId, resolvedPatientName);
    setCheckins(getCheckinsForPatient(resolvedPatientId, resolvedPatientName, lastCheckin));
  };

  const pending = sharedGapQuestions.filter((r) => r.question.patientStatus === 'PENDING_PATIENT');
  const answered = sharedGapQuestions.filter((r) => r.question.patientStatus === 'ANSWERED');
  const dueCheckins = checkins.filter((c) => c.isDue);

  const renderQuestionCard = (record: SharedGapQuestionRecord) => {
    const domain = DOMAIN_META[record.question.domain] ?? DOMAIN_META.SDOH;
    const severity = SEVERITY_CHIP[record.question.severity] ?? SEVERITY_CHIP.MEDIUM;
    const isAnswered = record.question.patientStatus === 'ANSWERED';

    return (
      <Grid item xs={12} key={`${record.analysisId}-${record.question.id}`}>
        <Card
          variant="outlined"
          sx={{
            bgcolor: isAnswered ? 'background.paper' : domain.color,
            borderLeft: 4,
            borderLeftColor: `${severity.color}.main`,
            opacity: isAnswered ? 0.75 : 1,
          }}
        >
          <CardContent>
            <Stack direction="row" spacing={1} flexWrap="wrap" alignItems="center" sx={{ mb: 1.5 }}>
              <Chip label={domain.label} size="small" color={domain.chipColor} />
              <Chip label={severity.label} size="small" color={severity.color} />
              {isAnswered ? (
                <Chip icon={<CheckCircleIcon />} label="Answered" size="small" color="success" variant="outlined" />
              ) : (
                <Chip icon={<HourglassTopIcon />} label="Awaiting response" size="small" color="warning" variant="outlined" />
              )}
            </Stack>

            <Typography variant="h6" sx={{ mb: 0.5 }}>
              {record.question.question}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
              {record.question.rationale}
            </Typography>

            {!isAnswered && (
              <Stack spacing={1.5}>
                <TextField
                  placeholder="Your response..."
                  multiline
                  rows={3}
                  value={responseDrafts[record.question.id] ?? ''}
                  onChange={(e) =>
                    setResponseDrafts((prev) => ({ ...prev, [record.question.id]: e.target.value }))
                  }
                />
                <Button
                  variant="contained"
                  size="small"
                  onClick={() => handleQuestionSubmit(record)}
                  disabled={!responseDrafts[record.question.id]?.trim()}
                >
                  Submit Response
                </Button>
              </Stack>
            )}
          </CardContent>
        </Card>
      </Grid>
    );
  };

  const renderCheckinCard = (checkin: CheckinNotification) => {
    type CheckinColorKey = 'daily' | 'weekly' | 'monthly';
    const colors: Record<CheckinColorKey, { bg: string; chipColor: 'error' | 'warning' | 'success' | 'info'; label: string }> = {
      daily: { bg: '#fff3cd', chipColor: 'warning', label: 'Daily' },
      weekly: { bg: '#d1ecf1', chipColor: 'info', label: 'Weekly' },
      monthly: { bg: '#d4edda', chipColor: 'success', label: 'Monthly' },
    };
    const style = colors[checkin.type];

    return (
      <Grid item xs={12} sm={6} md={4} key={checkin.id}>
        <Card
          variant="outlined"
          sx={{
            bgcolor: style.bg,
            borderLeft: 4,
            borderLeftColor: `${style.chipColor}.main`,
            cursor: 'pointer',
            transition: 'all 0.2s',
            '&:hover': { boxShadow: 2 },
          }}
        >
          <CardContent>
            <Stack spacing={2} alignItems="flex-start">
              <Chip
                icon={<EventIcon />}
                label={style.label}
                size="small"
                color={style.chipColor}
                variant="filled"
              />
              <Typography variant="h6">{checkin.label}</Typography>
              <Typography variant="body2" color="text.secondary">
                Share your progress for the past {style.label.toLowerCase()}.
              </Typography>
              {checkin.tasks.length > 0 && (
                <Stack
                  spacing={0.5}
                  sx={{
                    width: '100%',
                    pl: 1.5,
                    borderLeft: '3px solid',
                    borderLeftColor: `${style.chipColor}.main`,
                  }}
                >
                  {checkin.tasks.map((task) => (
                    <Box key={task.id} sx={{ mb: 0.5 }}>
                      <Typography variant="body2" fontWeight={task.required ? 600 : 400}>
                        {task.label}{task.required ? ' *' : ''}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {task.description}
                      </Typography>
                    </Box>
                  ))}
                  {checkin.tasks.some((t) => t.required) && (
                    <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                      * required
                    </Typography>
                  )}
                </Stack>
              )}
              <Button
                variant="contained"
                size="small"
                onClick={() => handleCheckinComplete()}
                fullWidth
              >
                Complete {style.label} Check-in
              </Button>
            </Stack>
          </CardContent>
        </Card>
      </Grid>
    );
  };

  const totalNotifications = pending.length + dueCheckins.length;

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 3 }}>
        <Box>
          <Typography variant="h4" gutterBottom>
            Notifications
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {totalNotifications === 0
              ? "You're all caught up!"
              : totalNotifications === 1
                ? '1 pending notification'
                : `${totalNotifications} pending notifications`}
          </Typography>
        </Box>
        <Chip
          label={totalNotifications}
          size="medium"
          color={totalNotifications > 0 ? 'warning' : 'default'}
          variant={totalNotifications > 0 ? 'filled' : 'outlined'}
        />
      </Stack>

      <Grid container spacing={2}>
        {/* Check-in Cards */}
        {dueCheckins.length > 0 && (
          <>
            <Grid item xs={12}>
              <Typography variant="h6" sx={{ mb: 1 }}>
                Check-ins ({dueCheckins.length})
              </Typography>
            </Grid>
            {dueCheckins.map((checkin) => renderCheckinCard(checkin))}
          </>
        )}

        {/* Questions from Nutritionist */}
        {pending.length > 0 && (
          <>
            <Grid item xs={12} sx={{ mt: dueCheckins.length > 0 ? 2 : 0 }}>
              <Divider sx={{ my: 1 }} />
              <Typography variant="h6" sx={{ mb: 1 }}>
                Questions from Your Care Team ({pending.length})
              </Typography>
            </Grid>
            {pending.map((record) => renderQuestionCard(record))}
          </>
        )}

        {/* Answered Questions (read-only archive) */}
        {answered.length > 0 && (
          <>
            <Grid item xs={12} sx={{ mt: 2 }}>
              <Divider sx={{ my: 1 }} />
              <Typography variant="h6" sx={{ mb: 1, color: 'text.secondary' }}>
                Answered Questions ({answered.length})
              </Typography>
            </Grid>
            {answered.map((record) => renderQuestionCard(record))}
          </>
        )}

        {/* Empty State */}
        {totalNotifications === 0 && answered.length === 0 && (
          <Grid item xs={12}>
            <Alert severity="success">No pending notifications. Great job staying on top of your health!</Alert>
          </Grid>
        )}
      </Grid>
    </Box>
  );
};
